import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "../hooks/useTranslation";
import { FiChevronLeft, FiChevronRight, FiTrendingUp, FiTrendingDown, FiTag, FiZoomIn } from "react-icons/fi";
import PSAlogo from "../images/psacard_logo.jpg";
import EbayLogo from "../images/EBay_logo.png";
import { API_BASE } from "../constants";
import { FaBook } from "react-icons/fa6";
import { AiOutlineInfoCircle } from "react-icons/ai";
import VaultDetailsBackground from "../images/vault_details_image.jpg";

const GET_NFT_LIBRARY_BY_ID_API = `${API_BASE}/publicApi/getNftLibraryById`;

// ─── Price Chart (SVG-based) ────────────────────────────────────────────────
const PriceChart = ({ data, color = "#5b47cd" }) => {
    const { t } = useTranslation();
    const [selectedYear, setSelectedYear] = useState("All");
    const [selectedMonth, setSelectedMonth] = useState("All");

    const MONTH_NAMES = [t('vault.jan'), t('vault.feb'), t('vault.mar'), t('vault.apr'), t('vault.may'), t('vault.jun'), t('vault.jul'), t('vault.aug'), t('vault.sep'), t('vault.oct'), t('vault.nov'), t('vault.dec')];

    const availableYears = useMemo(() => {
        if (!data) return [];
        const years = new Set(data.map(d => new Date(d.nph_date).getFullYear()));
        return Array.from(years).sort((a, b) => b - a);
    }, [data]);

    if (!data || data.length === 0) {
        return (
            <div style={{ width: "100%", height: "200px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "12px", color: "#555", fontSize: "14px" }}>
                {t('vault.noPriceHistory')}
            </div>
        );
    }

    const filteredData = data.filter(d => {
        const date = new Date(d.nph_date);
        if (selectedYear !== "All" && date.getFullYear().toString() !== selectedYear.toString()) return false;
        if (selectedMonth !== "All" && MONTH_NAMES[date.getMonth()] !== selectedMonth) return false;
        return true;
    }).sort((a, b) => new Date(a.nph_date) - new Date(b.nph_date));

    // Canvas dimensions
    const width = 600;
    const height = 400;
    const padL = 84;
    const padR = 20;
    const padT = 32;
    const padB = 84;
    const chartW = width - padL - padR;
    const chartH = height - padT - padB;

    let startDate, endDate, monthTicks;
    const today = new Date();

    if (selectedYear === "All" && selectedMonth === "All") {
        if (filteredData.length > 0) {
            const firstDate = new Date(filteredData[0].nph_date);
            startDate = new Date(firstDate.getFullYear(), firstDate.getMonth(), 1);
        } else {
            startDate = new Date(today.getFullYear(), today.getMonth() - 11, 1);
        }
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);

        // Ensure there's a valid range
        if (endDate.getTime() <= startDate.getTime()) {
            startDate = new Date(endDate.getFullYear(), endDate.getMonth() - 1, 1);
        }

        const monthsDiff = (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth());

        monthTicks = Array.from({ length: monthsDiff + 1 }, (_, i) => ({
            date: new Date(startDate.getFullYear(), startDate.getMonth() + i, 1)
        }));
    } else if (selectedYear !== "All" && selectedMonth === "All") {
        startDate = new Date(parseInt(selectedYear), 0, 1);
        endDate = new Date(parseInt(selectedYear), 11, 31);
        monthTicks = Array.from({ length: 12 }, (_, i) => ({
            date: new Date(parseInt(selectedYear), i, 1)
        }));
    } else if (selectedYear !== "All" && selectedMonth !== "All") {
        const mIdx = MONTH_NAMES.indexOf(selectedMonth);
        startDate = new Date(parseInt(selectedYear), mIdx, 1);
        endDate = new Date(parseInt(selectedYear), mIdx + 1, 0);

        const daysInMonth = endDate.getDate();
        monthTicks = [];
        for (let d = 1; d <= daysInMonth; d += Math.ceil(daysInMonth / 6)) {
            monthTicks.push({ date: new Date(parseInt(selectedYear), mIdx, d), label: `${d}` });
        }
    } else {
        const years = availableYears.length ? availableYears : [today.getFullYear()];
        const minYear = Math.min(...years);
        const maxYear = Math.max(...years);
        const mIdx = MONTH_NAMES.indexOf(selectedMonth);
        startDate = new Date(minYear, mIdx, 1);
        endDate = new Date(maxYear, mIdx + 1, 0);
        monthTicks = [];
        for (let y = minYear; y <= maxYear; y++) {
            monthTicks.push({ date: new Date(y, mIdx, 1) });
        }
    }

    const totalMs = Math.max(1, endDate.getTime() - startDate.getTime());
    const dateToX = (dateStr) => {
        const elapsed = new Date(dateStr).getTime() - startDate.getTime();
        return padL + Math.max(0, Math.min(1, elapsed / totalMs)) * chartW;
    };

    const monthTicksWithX = monthTicks.map(t => ({
        ...t,
        x: padL + ((t.date.getTime() - startDate.getTime()) / totalMs) * chartW
    }));

    let minVal = 0, maxVal = 100, valRange = 100, ptArr = [], ptStr = "", fillPt = "", latestData, latestPrice, latestDate, baseY;
    const gradId = `grad-${color.replace('#', '')}`;

    if (filteredData.length > 0) {
        const values = filteredData.map(d => parseFloat(d.nph_price) || 0);
        const minRaw = Math.min(...values);
        const maxRaw = Math.max(...values);
        const pv = (maxRaw === minRaw) ? (maxRaw > 0 ? maxRaw * 0.1 : 10) : (maxRaw - minRaw) * 0.15;
        minVal = Math.max(0, minRaw - pv);
        maxVal = maxRaw + pv;
        valRange = maxVal - minVal || 1;
        const valToY = (v) => padT + chartH - ((v - minVal) / valRange) * chartH;

        ptArr = filteredData.map(d => [dateToX(d.nph_date), valToY(parseFloat(d.nph_price) || 0)]);
        ptStr = ptArr.map(([x, y]) => `${x},${y}`).join(" ");
        baseY = padT + chartH;
        fillPt = `${ptArr[0][0]},${baseY} ${ptStr} ${ptArr[ptArr.length - 1][0]},${baseY}`;

        latestData = filteredData[filteredData.length - 1];
        latestPrice = parseFloat(latestData.nph_price).toLocaleString();
        latestDate = new Date(latestData.nph_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    }

    const isMobileLocal = window.innerWidth < 768;

    return (
        <div style={{ width: "100%", position: "relative" }}>
            <div style={{
                display: "flex",
                gap: "8px",
                marginBottom: isMobileLocal ? "20px" : "16px",
                justifyContent: isMobileLocal ? "flex-start" : "flex-end",
                position: isMobileLocal ? "relative" : "absolute",
                top: isMobileLocal ? 0 : -45,
                right: 0,
                zIndex: 10
            }}>
                <select
                    value={selectedYear}
                    onChange={e => setSelectedYear(e.target.value)}
                    style={{
                        background: "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(255,255,255,0.15)",
                        color: "#fff",
                        padding: "6px 12px",
                        borderRadius: "8px",
                        fontSize: "13px",
                        cursor: "pointer",
                        outline: "none"
                    }}
                >
                    <option value="All" style={{ background: "#111" }}>{t("vault.allYears")}</option>
                    {availableYears.map(y => <option key={y} value={y} style={{ background: "#111" }}>{y}</option>)}
                </select>
                <select
                    value={selectedMonth}
                    onChange={e => setSelectedMonth(e.target.value)}
                    style={{
                        background: "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(255,255,255,0.15)",
                        color: "#fff",
                        padding: "6px 12px",
                        borderRadius: "8px",
                        fontSize: "13px",
                        cursor: "pointer",
                        outline: "none"
                    }}
                >
                    <option value="All" style={{ background: "#111" }}>{t("vault.allMonths")}</option>
                    {MONTH_NAMES.map(m => <option key={m} value={m} style={{ background: "#111" }}>{m}</option>)}
                </select>
            </div>

            {filteredData.length === 0 ? (
                <div style={{ width: "100%", height: "200px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "12px", color: "#555", fontSize: "14px", border: "1px dashed rgba(255,255,255,0.1)", marginTop: 40 }}>
                    {t("vault.noDataForSelectedPeriod")}
                </div>
            ) : (
                <div style={{ position: "relative", width: "100%", paddingBottom: "68%", marginTop: isMobileLocal ? 0 : 40 }}>
                    <div style={{ position: "absolute", inset: 0 }}>
                        <svg
                            width="100%" height="100%"
                            viewBox={`0 0 ${width} ${height}`}
                            preserveAspectRatio="none"
                            style={{ display: "block", overflow: "visible" }}
                        >
                            <defs>
                                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={color} stopOpacity="0.35" />
                                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                                </linearGradient>
                            </defs>

                            {/* Horizontal grid lines + Y-axis price labels */}
                            {[0, 1, 2, 3].map(i => {
                                const y = padT + (i * chartH / 3);
                                const v = maxVal - (i * valRange / 3);
                                return (
                                    <g key={i}>
                                        <line
                                            x1={padL} y1={y} x2={width - padR} y2={y}
                                            stroke="rgba(255,255,255,0.06)" strokeWidth="1"
                                        />
                                        <text
                                            x={padL - 14} y={y + 7}
                                            fill="rgba(255,255,255,0.7)"
                                            fontSize="18" fontWeight="600" textAnchor="end"
                                        >
                                            ${Math.round(v).toLocaleString()}
                                        </text>
                                    </g>
                                );
                            })}

                            {/* Vertical dashed month separator lines */}
                            {monthTicksWithX.map(({ x }, idx) => (
                                <line key={idx}
                                    x1={x} y1={padT} x2={x} y2={baseY}
                                    stroke="rgba(255,255,255,0.05)" strokeWidth="1"
                                    strokeDasharray="3 4"
                                />
                            ))}

                            {/* Bottom axis line */}
                            <line
                                x1={padL} y1={baseY} x2={width - padR} y2={baseY}
                                stroke="rgba(255,255,255,0.15)" strokeWidth="1"
                            />

                            {/* Area fill */}
                            <polyline points={fillPt} fill={`url(#${gradId})`} />

                            {/* Price line */}
                            <polyline
                                points={ptStr}
                                fill="none" stroke={color}
                                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                            />

                            {/* Data point dots */}
                            {ptArr.map(([x, y], i) => (
                                <circle key={i} cx={x} cy={y} r="3"
                                    fill={color}
                                    stroke="rgba(0,0,0,0.6)" strokeWidth="1.5"
                                />
                            ))}

                            {/* Month labels (row 1) + Year labels (row 2, on year-change) */}
                            {monthTicksWithX.map(({ date, x, label }, idx) => {
                                const mon = MONTH_NAMES[date.getMonth()];
                                const yr = date.getFullYear();
                                const topText = label || mon;
                                const prevYr = idx === 0 ? null : monthTicksWithX[idx - 1].date.getFullYear();
                                const showYear = (!label && (idx === 0 || yr !== prevYr));
                                return (
                                    <g key={idx}>
                                        <text
                                            x={x} y={baseY + 30}
                                            fill="rgba(255,255,255,0.8)"
                                            fontSize="18" fontWeight="600" textAnchor="middle"
                                        >
                                            {topText}
                                        </text>
                                        {showYear && (
                                            <text
                                                x={x} y={baseY + 62}
                                                fill="#fff"
                                                fontSize="18" fontWeight="800"
                                                textAnchor="middle"
                                            >
                                                {yr}
                                            </text>
                                        )}
                                    </g>
                                );
                            })}
                        </svg>

                        {/* Latest-price tooltip */}
                        {latestData && (
                            <div style={{
                                position: "absolute", top: 8, right: 8,
                                background: "rgba(10,10,20,0.85)", backdropFilter: "blur(8px)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                padding: "8px 12px", borderRadius: "10px", fontSize: "12px",
                            }}>
                                <div style={{ color: "rgba(255,255,255,0.45)", marginBottom: 3 }}>{latestDate}</div>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: color }} />
                                    <span style={{ color: "#fff", fontWeight: 600 }}>${latestPrice}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────
export default function LibraryDetails() {
    const { t, language } = useTranslation();
    const { nl_id } = useParams();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [nftDetails, setNftDetails] = useState(null);
    const [isPositive, setIsPositive] = useState(false);
    const [priceHistory, setPriceHistory] = useState([]);
    const [showZoom, setShowZoom] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getNftDetails = useCallback(async () => {
        try {
            const response = await fetch(GET_NFT_LIBRARY_BY_ID_API, {
                method: 'POST',
                headers: { Accept: 'application/json' },
                body: JSON.stringify({ card_id: nl_id }),
            });
            const data = await response.json();
            if (!response.ok || !data.success) throw new Error(data.message || "Failed to fetch card items");

            const item = data.data || [];
            const price_history = data.priceHistory || [];
            setPriceHistory(price_history);

            let name = item.nl_name;
            let remark = item.nl_remark;
            switch (language) {
                case 'zh': name = item.nl_name_zh_CN || name; remark = item.nl_remark_zh_CN || remark; break;
                case 'zh-TW': name = item.nl_name_zh_TW || name; remark = item.nl_remark_zh_TW || remark; break;
                case 'ko': name = item.nl_name_ko_KR || name; remark = item.nl_remark_ko_KR || remark; break;
                case 'ja': name = item.nl_name_ja_JP || name; remark = item.nl_remark_ja_JP || remark; break;
                case 'vi': name = item.nl_name_vi_VN || name; remark = item.nl_remark_vi_VN || remark; break;
                case 'id': name = item.nl_name_id_ID || name; remark = item.nl_remark_id_ID || remark; break;
                case 'ta': name = item.nl_name_ta_IN || name; remark = item.nl_remark_ta_IN || remark; break;
                case 'hi': name = item.nl_name_hi_IN || name; remark = item.nl_remark_hi_IN || remark; break;
                default: break;
            }

            const price_changes = parseFloat(item.nl_value) - parseFloat(item.nl_last_value);
            setIsPositive(price_changes >= 0);
            const price_change_percent = (price_changes / parseFloat(item.nl_value)) * 100;
            const current_value = parseFloat(item.nl_owned) * parseFloat(item.nl_value);

            setNftDetails({
                ...item, name, remark,
                price_changes, price_change_percent, current_value,
            });
        } catch (error) {
            console.error('Error fetching NFT details:', error);
        }
    }, [nl_id, language]);

    useEffect(() => { getNftDetails(); }, [getNftDetails]);

    // ── Loading state ──
    if (!nftDetails) {
        return (
            <div style={{ minHeight: "100vh", background: "#070710", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{
                    width: 48, height: 48, borderRadius: "50%",
                    border: "3px solid transparent",
                    borderTopColor: "#6366f1",
                    borderRightColor: "#818cf8",
                    animation: "spin 0.8s linear infinite"
                }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
            </div>
        );
    }

    // ── Derived values ──
    const trendColor = isPositive ? "#10b981" : "#ef4444";
    const trendBg = isPositive ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.12)";

    // ── Style helpers ──
    const pillTag = {
        display: "inline-flex", alignItems: "center",
        padding: "4px 10px",
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: "20px",
        fontSize: "12px", color: "rgba(255,255,255,0.75)",
        whiteSpace: "nowrap",
    };

    const sectionBox = {
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: "16px",
        padding: "20px",
        marginBottom: "16px",
    };

    const statCard = {
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: "12px",
        padding: "16px",
        display: "flex", flexDirection: "column", gap: "6px",
    };

    // ── Metadata tags array ──
    const metaTags = [
        nftDetails.nl_grader && `${t("vault.grader")}: ${nftDetails.nl_grader}`,
        nftDetails.nl_serial_no && `${t("vault.serialNo")}: ${nftDetails.nl_serial_no}`,
        nftDetails.nl_grade && `${t("vault.grade")}: ${nftDetails.nl_grade}`,
        nftDetails.nl_year && `${t("vault.year")}: ${nftDetails.nl_year}`,
        nftDetails.nl_hp && `${t("vault.hp")}: ${nftDetails.nl_hp}`,
    ].filter(Boolean);

    return (
        <div style={{
            minHeight: "100%",
            background: "#070710",
            color: "#fff",
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            paddingBottom: isMobile ? "120px" : "60px",
            position: "relative",
            overflow: "hidden",
        }}>
            {/* ── Ambient side glows ── */}
            <div style={{
                position: "fixed", top: 0, left: 0, width: "180px", height: "100vh",
                background: "linear-gradient(to right, rgba(60,80,200,0.18) 0%, transparent 100%)",
                pointerEvents: "none", zIndex: 0,
            }} />
            <div style={{
                position: "fixed", top: 0, right: 0, width: "180px", height: "100vh",
                background: "linear-gradient(to left, rgba(60,80,200,0.18) 0%, transparent 100%)",
                pointerEvents: "none", zIndex: 0,
            }} />

            {/* ── Content wrapper ── */}
            <div style={{ position: "relative", zIndex: 1, maxWidth: isMobile ? "100%" : "1100px", margin: "0 auto" }}>

                {/* ═══ HERO SECTION ═══ */}
                <div style={{ position: "relative", width: "100%", overflow: "hidden", marginBottom: "24px" }}>
                    <div style={{
                        position: "absolute", inset: 0,
                        backgroundImage: `url(${VaultDetailsBackground})`,
                        backgroundSize: "cover", backgroundPosition: "center",
                    }} />
                    {/* Dark overlay gradient at bottom */}
                    <div style={{
                        position: "absolute", bottom: 0, left: 0, right: 0, height: "50%",
                        background: "linear-gradient(to bottom, transparent, #070710)",
                    }} />

                    {/* Top controls */}
                    <div style={{
                        position: "relative", zIndex: 2,
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "16px",
                    }}>
                        <button
                            onClick={() => navigate(-1)}
                            style={{
                                width: 40, height: 40, borderRadius: "50%",
                                background: "rgba(0,0,0,0.55)",
                                backdropFilter: "blur(10px)",
                                border: "1px solid rgba(255,255,255,0.35)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                cursor: "pointer", color: "#fff",
                            }}
                        >
                            <FiChevronLeft size={20} />
                        </button>
                        <div style={{ display: "flex", gap: 10 }}>
                            <button
                                onClick={() => setShowZoom(true)}
                                style={{
                                    width: 40, height: 40, borderRadius: "50%",
                                    background: "rgba(0,0,0,0.55)",
                                    backdropFilter: "blur(10px)",
                                    border: "1px solid rgba(255,255,255,0.35)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    cursor: "pointer", color: "#fff",
                                }}
                            >
                                <FiZoomIn size={18} />
                            </button>
                        </div>
                    </div>

                    {/* Card image – centered */}
                    <div style={{
                        position: "relative", zIndex: 2,
                        display: "flex", justifyContent: "center",
                        padding: isMobile ? "0 40px 32px" : "0 120px 48px",
                    }}>
                        <img
                            src={nftDetails.nl_image3}
                            alt={nftDetails.name}
                            style={{
                                width: "100%",
                                maxWidth: isMobile ? "260px" : "320px",
                                borderRadius: "12px",
                                boxShadow: "0 24px 80px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.08)",
                                display: "block",
                            }}
                        />
                    </div>
                </div>

                {/* ═══ INFO SECTION (below hero) ═══ */}
                <div style={{ padding: isMobile ? "0 16px" : "0 24px" }}>

                    {/* Card title */}
                    <h1 style={{
                        fontSize: isMobile ? "20px" : "28px",
                        fontWeight: 600,
                        color: "#fff",
                        marginBottom: "14px",
                        lineHeight: 1.3,
                    }}>
                        {nftDetails.name}
                    </h1>

                    {/* Metadata pill tags */}
                    {metaTags.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "20px" }}>
                            {metaTags.map((tag, i) => (
                                <span key={i} style={pillTag}>{tag}</span>
                            ))}
                        </div>
                    )}


                    {/* ── MARKET VALUE CARD ── */}
                    <div style={{
                        ...sectionBox,
                        background: "rgba(255,255,255,0.05)",
                        borderColor: "rgba(255,255,255,0.09)",
                    }}>
                        <div style={{
                            fontSize: '12px', fontWeight: 600, letterSpacing: "1.5px",
                            color: "rgba(255,255,255,0.4)", textTransform: "uppercase",
                            marginBottom: "8px",
                        }}>
                            {t("vault.marketValue") || "MARKET VALUE"}
                        </div>
                        <div style={{
                            fontSize: isMobile ? "32px" : "40px",
                            fontWeight: 600, color: "#fff", marginBottom: "8px",
                        }}>
                            ${Number(nftDetails.nl_value).toLocaleString()}
                        </div>
                        <div style={{
                            display: "inline-flex", alignItems: "center", gap: "6px",
                            padding: "4px 10px", borderRadius: "20px",
                            background: trendBg, fontSize: "13px", fontWeight: 600, color: trendColor,
                        }}>
                            {isPositive ? <FiTrendingUp size={14} /> : <FiTrendingDown size={14} />}
                            {isPositive ? "+" : ""}${Math.abs(nftDetails.price_changes).toLocaleString()} ({isPositive ? "+" : ""}{nftDetails.price_change_percent.toFixed(2)}%)
                        </div>
                    </div>

                    {/* Remark */}
                    {nftDetails.nl_remark && (
                        <div style={{ marginBottom: "20px" }}>
                            {/* Description header */}
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                                <div style={{
                                    width: 32, height: 32, borderRadius: "8px",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    flexShrink: 0,
                                }}>
                                    <FaBook size={20} color="#5b47cd" />
                                </div>
                                <span style={{ fontSize: "16px", fontWeight: 600, color: "#fff" }}>
                                    {t("vault.description") || "Description"}
                                </span>
                            </div>
                            {/* Description text */}
                            <div
                                style={{
                                    fontSize: "14px",
                                    color: "rgba(255,255,255,0.55)",
                                    lineHeight: 1.7,
                                    wordBreak: "break-word"
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: nftDetails.nl_remark
                                        ? nftDetails.nl_remark.replace(/ {2}/g, ' &nbsp;').replace(/\n/g, '<br />')
                                        : ''
                                }}
                            />
                        </div>
                    )}

                    {/* ── DESKTOP: Two-column layout for middle & right ── */}
                    {!isMobile ? (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: "20px" }}>
                            {/* Left column */}
                            <div>
                                {/* Price History */}
                                <div style={sectionBox}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                                        <FiTrendingUp color="#5b47cd" size={16} />
                                        <h3 style={{ fontSize: "15px", fontWeight: 600, color: "#fff" }}>{t("vault.priceHistory")}</h3>
                                    </div>
                                    <PriceChart data={priceHistory} />
                                </div>

                                {/* Info Stats */}
                                <div style={sectionBox}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                                        <AiOutlineInfoCircle color="#5b47cd" size={16} />
                                        <h3 style={{ fontSize: "15px", fontWeight: 600, color: "#fff" }}>{t("vault.Info")}</h3>
                                    </div>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                                        {[
                                            { label: t("vault.owned"), value: nftDetails.nl_owned || 0 },
                                            // { label: t("vault.buyInValue"), value: `$${Number(nftDetails.nl_buy_in_value || 0).toLocaleString()}` },
                                            { label: t("vault.currentValue"), value: `$${Number(nftDetails.current_value || 0).toLocaleString()}` },
                                            // { label: t("vault.earning"), value: `$${Number(nftDetails.nl_earning || 0).toLocaleString()}` },
                                        ].map((s, i) => (
                                            <div key={i} style={statCard}>
                                                <span style={{ fontSize: '12px', fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.8px", color: "rgba(255,255,255,0.4)" }}>{s.label}</span>
                                                <span style={{ fontSize: "22px", fontWeight: 600, color: "#5b47cd" }}>{s.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Right column – price check */}
                            <div>
                                <div style={sectionBox}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
                                        <FiTag color="#5b47cd" size={16} />
                                        <h3 style={{ fontSize: "15px", fontWeight: 600, color: "#fff" }}>{t("vault.priceCheck")}</h3>
                                    </div>
                                    {[
                                        { logo: PSAlogo, alt: "PSA", tagline: t("vault.grabYourCardToday"), link: nftDetails.nl_link_psa, icon: <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><FiChevronRight size={16} color="#fff" /></div> },
                                        { logo: EbayLogo, alt: "eBay", tagline: t("vault.wantToSellThisItem"), link: nftDetails.nl_link_ebay2, icon: <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><FiChevronRight size={16} color="#fff" /></div> },
                                    ].map((shop, i) => (
                                        <div key={i} onClick={() => shop.link && window.open(shop.link)} style={{
                                            display: "flex", alignItems: "center", gap: "12px",
                                            padding: "14px 0",
                                            borderBottom: i === 0 ? "1px solid rgba(255,255,255,0.07)" : "none",
                                            cursor: shop.link ? "pointer" : "default",
                                        }}>
                                            <img src={shop.logo} alt={shop.alt} style={{ height: 28, objectFit: "contain", flexShrink: 0 }} />
                                            <span style={{ flex: 1, fontSize: "13px", color: "rgba(255,255,255,0.5)" }}>{shop.tagline}</span>
                                            {shop.icon}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* ── MOBILE: Single column ── */
                        <>
                            {/* Price History */}
                            <div style={sectionBox}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                                    <FiTrendingUp color="#5b47cd" size={16} />
                                    <h3 style={{ fontSize: "14px", fontWeight: 600, color: "#fff" }}>{t("vault.priceHistory")}</h3>
                                </div>
                                <PriceChart data={priceHistory} />
                            </div>

                            {/* Info Stats */}
                            <div style={sectionBox}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                                    <AiOutlineInfoCircle color="#5b47cd" size={20} />
                                    <h3 style={{ fontSize: "14px", fontWeight: 600, color: "#fff" }}>{t("vault.Info")}</h3>
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                                    {[
                                        { label: t("vault.owned"), value: nftDetails.nl_owned || 0 },
                                        // { label: t("vault.buyInValue"), value: `$${Number(nftDetails.nl_buy_in_value || 0).toLocaleString()}` },
                                        { label: t("vault.currentValue"), value: `$${Number(nftDetails.current_value || 0).toLocaleString()}` },
                                        // { label: t("vault.earning"), value: `$${Number(nftDetails.nl_earning || 0).toLocaleString()}` },
                                    ].map((s, i) => (
                                        <div key={i} style={statCard}>
                                            <span style={{ fontSize: '12px', fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", color: "rgba(255,255,255,0.4)" }}>{s.label}</span>
                                            <span style={{ fontSize: "18px", fontWeight: 600, color: "#5b47cd" }}>{s.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Price Check */}
                            <div style={sectionBox}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                                    <FiTag color="#5b47cd" size={16} />
                                    <h3 style={{ fontSize: "14px", fontWeight: 600, color: "#fff" }}>{t("vault.priceCheck")}</h3>
                                </div>
                                {[
                                    { logo: PSAlogo, alt: "PSA", tagline: t("vault.grabYourCardToday"), link: nftDetails.nl_link_psa, icon: <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><FiChevronRight size={16} color="#fff" /></div> },
                                    { logo: EbayLogo, alt: "eBay", tagline: t("vault.wantToSellThisItem"), link: nftDetails.nl_link_ebay2, icon: <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><FiChevronRight size={16} color="#fff" /></div> },
                                ].map((shop, i) => (
                                    <div key={i} onClick={() => shop.link && window.open(shop.link)} style={{
                                        display: "flex", alignItems: "center", gap: "12px",
                                        padding: "13px 0",
                                        borderBottom: i === 0 ? "1px solid rgba(255,255,255,0.07)" : "none",
                                        cursor: shop.link ? "pointer" : "default",
                                    }}>
                                        <img src={shop.logo} alt={shop.alt} style={{ height: 26, objectFit: "contain", flexShrink: 0 }} />
                                        <span style={{ flex: 1, fontSize: "13px", color: "rgba(255,255,255,0.5)" }}>{shop.tagline}</span>
                                        {shop.icon}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* ── Zoom Lightbox Modal ── */}
            {showZoom && (
                <div
                    onClick={() => setShowZoom(false)}
                    style={{
                        position: "fixed", inset: 0, zIndex: 47,
                        background: "rgba(0,0,0,0.92)",
                        backdropFilter: "blur(16px)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        padding: "24px",
                        animation: "fadeInZoom 0.2s ease",
                    }}
                >
                    <style>{`
                        @keyframes fadeInZoom {
                            from { opacity: 0; transform: scale(0.94); }
                            to   { opacity: 1; transform: scale(1); }
                        }
                    `}</style>

                    {/* Close button */}
                    <button
                        onClick={(e) => { e.stopPropagation(); setShowZoom(false); }}
                        style={{
                            position: "absolute", top: 20, right: 20,
                            width: 40, height: 40, borderRadius: "50%",
                            background: "rgba(255,255,255,0.12)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            color: "#fff", fontSize: "20px", lineHeight: 1,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: "pointer",
                        }}
                    >
                        ✕
                    </button>

                    {/* Full-size card image */}
                    <img
                        src={nftDetails.nl_image3}
                        alt={nftDetails.name}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            maxWidth: "90vw",
                            maxHeight: "88vh",
                            objectFit: "contain",
                            borderRadius: "16px",
                            boxShadow: "0 32px 100px rgba(0,0,0,0.9)",
                        }}
                    />
                </div>
            )}
        </div>
    );
}